import { useBounds, useGLTF } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { forwardRef, useEffect } from "react";
import { Box3, FrontSide, Vector3 } from "three";
import { centralizeControls, centralizeModel, initializeRotation } from "../ModelPreview";

const ModelGLTF = forwardRef(({ url, data, adjustScale, rotation, onLoad, setAxisThickness, onScreenshot, intl }, ref) => {
  const { scene: model } = useGLTF(url);

  const { meshRef, controlRef, sceneRef, lineRef } = ref;
  const { gl, scene } = useThree();
  const bounds = useBounds();

  /**
   * Set the sceneRef.
   */
  useEffect(() => {
    if (scene) {
      sceneRef.current = scene;
    } else {
      sceneRef.current = null;
    }
  }, [scene]);

  /**
   * Enables / disables loading-screen.
   */
  useEffect(() => {
    if (model) {
      // disable loading-screen
      onLoad(true);
    } else {
      // enable loading-screen
      onLoad(false);
    }
  }, [model]);

  /**
   * Takes a screenshot of the current model.
   */
  useEffect(() => {
    if (onScreenshot && model && gl) {
      onScreenshot(gl);
    }
  }, [model, gl]);

  /**
   * Prepare the materials for viewing.
   */
  useEffect(() => {
    if (model) {
      model.renderOrder = 500;
      const lightEl = [];
      model.traverse(child => {
        if (child.isLight) {
          lightEl.push(child);
        } else if (child.isMesh) {
          const childMesh = child;
          childMesh.renderOrder = 500;
          childMesh.castShadow = true;
          childMesh.receiveShadow = false;

          const materials = Array.isArray(childMesh.material)
            ? childMesh.material
            : [childMesh.material];
          materials.forEach((material) => {
            material.depthWrite = true;
            material.depthTest = true;
            material.side = FrontSide;
            material.shadowSide = FrontSide;
          });
        }
      });
      lightEl.forEach(el => {
        el.parent?.remove(el);
      });
    }
  }, [model]);

  /**
   * Returns the maximum coordinate of the model.
   * @param {*} model 
   * @returns 
   */
  const getMaximumCoordinate = (model) => {
    const box3 = new Box3().setFromObject(model);
    const size = new Vector3();
    box3.getSize(size);
    return Math.max(size.x, size.y, size.z);
  };

  /**
   * Centralize the model in the beginning.
   */
  useEffect(() => {
    initializeRotation(model, rotation);
    centralizeModel(model, lineRef, scene, data, intl);
    centralizeControls(controlRef, bounds, gl);
    // update axis-thickness
    if (setAxisThickness && model) {
      const maximum = getMaximumCoordinate(model);
      setAxisThickness(maximum * 0.004);
    }
  }, [model, gl, scene, bounds, data.scale]);

  useEffect(() => {
    if (!model) return;
    const maximum = getMaximumCoordinate(model);
    if (maximum > 50) {
      // adjust the scale according to maximum coordinate
      adjustScale(maximum);
    }
  }, [model]);

  // useFrame((state, delta) => {
  //   modelRef.current.rotation.y += delta / 5;
  // });

  return (
    <primitive
      ref={meshRef}
      object={model}
      scale={(data.scale === undefined || data.scale === 0) ? 1.0 : data.scale}
    />
  );
});

export default ModelGLTF;